import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { Calendar } from 'lucide-react';

const DateInput = React.forwardRef(({
  className,
  label,
  description,
  error,
  required = false,
  id,
  value = '',
  onChange,
  placeholder = 'DD-MM-YYYY',
  ...props
}, ref) => {
  const [displayValue, setDisplayValue] = useState('');
  const [internalError, setInternalError] = useState('');
  
  // Generate unique ID if not provided
  const inputId = id || `date-input-${Math.random()?.toString(36)?.substr(2, 9)}`;

  // Convert ISO date to DD-MM-YYYY format
  const formatDateToDisplay = (isoDate) => {
    if (!isoDate) return '';
    
    // Handle ISO format (YYYY-MM-DD)
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = isoDate?.match(dateRegex);
    
    if (match) {
      const [, year, month, day] = match;
      return `${day}-${month}-${year}`;
    }
    
    return isoDate;
  };

  // Convert DD-MM-YYYY to ISO format (YYYY-MM-DD)
  const formatDateToISO = (displayDate) => {
    if (!displayDate) return '';
    
    const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = displayDate?.match(dateRegex);
    
    if (match) {
      const [, day, month, year] = match;
      return `${year}-${month}-${day}`;
    }
    
    return displayDate;
  };

  // Validate date
  const isValidDate = (dateString) => {
    const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = dateString?.match(dateRegex);
    
    if (!match) return false;
    
    const [, day, month, year] = match;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    
    // Basic validation
    if (monthNum < 1 || monthNum > 12) return false;
    if (dayNum < 1 || dayNum > 31) return false;
    if (yearNum < 1900 || yearNum > 2100) return false;
    
    // More precise validation using Date object
    const date = new Date(yearNum, monthNum - 1, dayNum);
    return (
      date?.getFullYear() === yearNum &&
      date?.getMonth() === monthNum - 1 &&
      date?.getDate() === dayNum
    );
  };

  // Initialize display value from prop value
  useEffect(() => {
    if (value) {
      setDisplayValue(formatDateToDisplay(value));
    }
  }, [value]);

  // Handle input change with auto-formatting
  const handleInputChange = (e) => {
    let inputValue = e?.target?.value;
    
    // Remove any non-numeric characters except -
    inputValue = inputValue?.replace(/[^\d\-]/g, '');
    
    // Auto-add hyphens
    if (inputValue?.length === 2 && !inputValue?.includes('-')) {
      inputValue += '-';
    } else if (inputValue?.length === 5 && inputValue?.split('-')?.length === 2) {
      const parts = inputValue?.split('-');
      inputValue = `${parts?.[0]}-${parts?.[1]}-`;
    }
    
    // Limit to DD-MM-YYYY format length
    if (inputValue?.length > 10) {
      inputValue = inputValue?.substring(0, 10);
    }
    
    setDisplayValue(inputValue);
    setInternalError('');
    
    // If complete date entered, validate and call onChange
    if (inputValue?.length === 10) {
      if (isValidDate(inputValue)) {
        const isoDate = formatDateToISO(inputValue);
        onChange?.({ target: { value: isoDate, name: e?.target?.name } });
      } else {
        setInternalError('Invalid date. Please use DD-MM-YYYY format');
      }
    } else if (inputValue?.length === 0) {
      // Clear the value
      onChange?.({ target: { value: '', name: e?.target?.name } });
    }
  };

  // Handle blur to validate final input
  const handleBlur = (e) => {
    if (displayValue && displayValue?.length > 0 && displayValue?.length < 10) {
      setInternalError('Please enter a complete date (DD-MM-YYYY)');
    } else if (displayValue?.length === 10 && !isValidDate(displayValue)) {
      setInternalError('Invalid date. Please check day, month, and year');
    }
    
    props?.onBlur?.(e);
  };

  const finalError = error || internalError;

  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
            finalError ? "text-destructive" : "text-foreground"
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          type="text"
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            finalError && "border-destructive focus-visible:ring-destructive",
            className
          )}
          ref={ref}
          id={inputId}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          {...props}
        />
        <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>

      {description && !finalError && (
        <p className="text-sm text-muted-foreground">
          {description}
        </p>
      )}

      {finalError && (
        <p className="text-sm text-destructive">
          {finalError}
        </p>
      )}
    </div>
  );
});

DateInput.displayName = "DateInput";

export default DateInput;